import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LogOut } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';

const CONFIRM_WORD = 'verlaten';

interface LeaveSamenGroupSectionProps {
  groupName: string;
  memberCount: number;
  onLeft?: () => void;
}

/**
 * Quiet danger zone at the foot of the samen page, matching how account
 * deletion is presented on the profile page. Deliberately not next to the
 * group name and photo -- those are click-to-edit controls people use
 * casually, and this is not an action to put within misclicking distance.
 *
 * Leaving as the last member doesn't delete the group there and then: it
 * starts a 30-day countdown, so an accidental exit doesn't take the chat
 * history with it and anyone holding a pending invite can still bring the
 * group back. That case asks for the confirmation word; leaving a group that
 * carries on without you is a smaller thing and just asks.
 */
export function LeaveSamenGroupSection({
  groupName,
  memberCount,
  onLeft,
}: LeaveSamenGroupSectionProps) {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const isLastMember = memberCount <= 1;
  const canConfirm =
    !isLastMember || confirmText.trim().toLowerCase() === CONFIRM_WORD;

  const handleConfirm = async () => {
    if (!canConfirm || submitting) return;
    setSubmitting(true);
    try {
      const { error } = await supabase.rpc('leave_samen_group');
      if (error) throw error;

      toast({
        title: 'Je hebt de groep verlaten',
        description: isLastMember
          ? 'Je was het laatste lid. De groep wordt over 30 dagen verwijderd.'
          : `Je maakt geen deel meer uit van ${groupName}.`,
      });
      onLeft?.();
    } catch (err) {
      console.error('Failed to leave samen group:', err);
      toast({
        variant: 'destructive',
        title: 'Verlaten mislukt',
        description: 'Probeer het opnieuw of neem contact op.',
      });
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-8 pt-6 border-t border-border">
      <div className="flex flex-col items-center gap-2 text-center">
        <h4 className="text-sm font-medium text-muted-foreground">
          Groep verlaten
        </h4>
        <p className="text-xs text-muted-foreground max-w-md">
          Je ziet de chat en geplande activiteiten dan niet meer. Daarna kun je
          zelf een groep starten of je door iemand anders laten uitnodigen.
        </p>
        <Button
          variant="ghost"
          size="sm"
          className="text-red-500 hover:text-red-600 hover:bg-red-50"
          onClick={() => setOpen(true)}
        >
          <LogOut className="mr-2 h-4 w-4" />
          Verlaat deze groep
        </Button>
      </div>

      <AlertDialog
        open={open}
        onOpenChange={(next) => {
          if (!submitting) {
            setOpen(next);
            if (!next) setConfirmText('');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {groupName} verlaten?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                {isLastMember ? (
                  <>
                    <p>
                      Je bent het laatste lid. Als je weggaat wordt de groep,
                      samen met de chat en de geplande activiteiten, over 30
                      dagen verwijderd.
                    </p>
                    <p>
                      Komt er binnen die 30 dagen iemand bij &mdash; bijvoorbeeld
                      via een uitnodiging die nog openstaat &mdash; dan blijft de
                      groep gewoon bestaan.
                    </p>
                    <p>
                      Typ{' '}
                      <span className="font-mono font-semibold text-red-600">
                        {CONFIRM_WORD}
                      </span>{' '}
                      hieronder om te bevestigen.
                    </p>
                    <Input
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder={CONFIRM_WORD}
                      autoComplete="off"
                      disabled={submitting}
                    />
                  </>
                ) : (
                  <>
                    <p>
                      Je ziet de chat en de geplande activiteiten van deze groep
                      daarna niet meer. De groep zelf blijft bestaan voor de
                      anderen.
                    </p>
                    <p>
                      Om er weer bij te komen heb je een nieuwe uitnodiging van
                      een van hen nodig.
                    </p>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>
              Annuleren
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={!canConfirm || submitting}
              onClick={handleConfirm}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {submitting ? 'Bezig...' : 'Verlaat de groep'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
